import React, { useEffect } from "react";
import { styled } from "styled-components";
import { useEditor, useNode } from "@craftjs/core";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import { ReactComponent as MoveIcon } from "@assets/images/segmentEditor/moveBlock.svg";
import { useDispatch, useSelector } from "react-redux";
import { setToolBarVisible } from "../../../../redux/reducers/segmentBuilder";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import { useClone } from "../../../../utils/contentBlocksEditorUtils";
import { colors } from "../../../../utils";
import { openModal } from "../../../../redux/reducers/dialog";
import { handleMenuChange } from "../../../../redux/reducers/segmentBuilder";
import {
  getSelectedMenu,
  getWheelSegments,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { openMoveBlockModal } from "../../../../redux/reducers/moveBlockDialog";
import { deleteWheelMedia } from "../../../../services/api";
import { useParams } from "react-router-dom";
import { MEDIA_LIFECYCLE_SAFE_DELETE_ENABLED } from "../../../../constants/featureFlags";
import { collectDeletableMediaUrls } from "../../../../utils/mediaLifecycle";

const IconsView = styled.div`
  width: max-content;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0px 5px;
  height: 28px;
  right: -2px;
  position: absolute;
  z-index: 10;
  top: -28px;
`;

const IconGlyph = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  svg {
    color: white !important;
    cursor: pointer;
  }
  /* Visible keyboard focus ring on the (previously div-only) action buttons. */
  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
`;

// EW a11y: the block toolbar's actions were bare <div>s wrapping an SVG — no
// role, no accessible name, not focusable — so a screen reader announced
// nothing and a keyboard user could not reach or fire them. This gives each one
// button semantics: a role, an accessible name (aria-label + title tooltip),
// Tab focus, and Enter/Space activation. Ref is forwarded so the craft.js drag
// connector can still attach to the drag handle.
const IconButton = React.forwardRef(function IconButton(
  { label, onClick, children, ...rest },
  ref
) {
  const handleKeyDown = (event) => {
    if (!onClick) return;
    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      onClick(event);
    }
  };
  return (
    <IconGlyph
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={label}
      title={label}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </IconGlyph>
  );
});

export const BlockIndicator = ({ children }) => {
  const { id } = useNode();
  const { actions, query, isActive } = useEditor((_, query) => ({
    isActive: query.getEvent("selected").contains(id),
  }));

  const { id: wheelId } = useParams();

  const {
    connectors: { drag },
    moveable,
    deletable,
    isHovered,
    data,
  } = useNode((node) => ({
    isHovered: node.events.hovered,
    moveable: query.node(node.id).isDraggable(),
    deletable: query.node(node.id).isDeletable(),
    data: node.data,
  }));


  const selectedMenu = useSelector(getSelectedMenu);
  const wheelData = useSelector(getWheelSegments);

  const { handleClone } = useClone(query, actions);
  const dispatch = useDispatch();

  useEffect(() => {
    if (isActive) dispatch(handleMenuChange({ num: 3 }));
  }, [isActive]);

  useEffect(() => {
    if (selectedMenu == 1 || selectedMenu == 2) actions.selectNode(null);
  }, [selectedMenu]);

  const onEditClick = () => {
    dispatch(handleMenuChange({ num: 3 }));
    dispatch(toggleSegmentSettings(true));
    actions.selectNode(id);
  };

  // EW-010: refcount media against the rest of the current wheel BEFORE the
  // node is removed, so we never over-delete media a surviving block still
  // uses. Snapshotting the serialized tree first (delete is fired afterwards)
  // gives us every other block's props for the refcount.
  const collectSafeMediaUrls = () => {
    let survivingBlocks = [];
    try {
      const serialized = query.getSerializedNodes();
      survivingBlocks = Object.entries(serialized)
        .filter(([nodeId]) => nodeId !== id)
        .map(([, node]) => ({
          name: node?.type?.resolvedName || node?.displayName || node?.type,
          props: node?.props,
        }));
    } catch (e) {
      // If the tree can't be read, fall back to no surviving refs (releases
      // exactly the deleted block's own urls — same as the legacy behavior).
      survivingBlocks = [];
    }
    return collectDeletableMediaUrls(
      { name: data?.name, props: data?.props },
      survivingBlocks
    );
  };

  const onConfirmDeleteSafe = async () => {
    const urlsToRelease = collectSafeMediaUrls();
    actions.delete(id);
    try {
      for (const url of urlsToRelease) {
        await deleteWheelMedia({ url, wheel_id: wheelId });
      }
    } catch (error) {
      // QA-113 (Kelly, 2026-08-06): the auto-dismissing "media could not be
      // released / may still count toward your storage" toast was removed — it
      // flashed too fast to read and alarmed the user over a background cleanup
      // failure they cannot act on. The failure is still logged for diagnostics;
      // a proper out-of-space / storage prompt is a separate follow-up.
      console.error("Error deleting media:", error);
    }
  };

  const onConfirmDeleteLegacy = async () => {
    console.log("Deleting node:", data?.name, data?.props);
    try {
      actions.delete(id);
      if (data?.name === "ImageWidget" || data?.name === "ImageWithText") {
        const response = await deleteWheelMedia({
          url: data?.props?.image?.src,
          wheel_id: wheelId,
        });
        console.log("Delete API response:", response);
      }
      if (data?.name === "VideoWidget") {
        const response = await deleteWheelMedia({
          url: data?.props?.src,
          wheel_id: wheelId,
        });
        console.log("Delete API response:", response);
      }
      if (data?.name === "ImageGridWidget" || data?.name === "CarouselWidget") {
        for (let i = 0; i < data?.props?.images?.length; i++) {
          const response = await deleteWheelMedia({
            url: data?.props?.images[i]?.src,
            wheel_id: wheelId,
          });
          console.log("Delete API response:", response);
        }
      }
      if (data?.name === "DocumentWidget") {
        for (let i = 0; i < data?.props?.documents?.length; i++) {
          const response = await deleteWheelMedia({
            url: data?.props?.documents[i]?.src,
            wheel_id: wheelId,
          });
          console.log("Delete API response:", response);
        }
      }
      if (data?.name === "AudioWidget") {
        for (let i = 0; i < data?.props?.audios?.length; i++) {
          const response = await deleteWheelMedia({
            url: data?.props?.audios[i]?.src,
            wheel_id: wheelId,
          });
          console.log("Delete API response:", response);
        }
      }
    } catch (error) {
      console.error("Error deleting media:", error);
      // Show error toast or notification here if needed
    }
  };

  const onConfirmDelete = MEDIA_LIFECYCLE_SAFE_DELETE_ENABLED
    ? onConfirmDeleteSafe
    : onConfirmDeleteLegacy;

  const onDeleteClick = () => {
    dispatch(
      openModal({
        onConfirm: onConfirmDelete,
        title: "Delete block",
        subtitle: "Are you sure you want to delete this block?",
      })
    );
  };

  const handleMove = () => {
    dispatch(openMoveBlockModal({ blockId: id }));
  };

  const color = isActive ? "#2196F3" : colors.purple;
  const showToolbar = isHovered || isActive;

  return (
    <div
      data-testid="block-indicator-wrapper"
      style={{
        position: "relative",
        border: isHovered ? `2px  ${color} dashed` : `2px solid transparent`,
        // gordon-0923-P0-REPEAT: IconsView (top:-28px, position:absolute) is
        // meant to be a pure overlay, same as Production — it must cover
        // whatever sits above it, not push this block down to make room. A
        // prior fix (hw-0914) added a conditional marginTop here to stop the
        // toolbar visually overlapping the previous block's text, but that
        // reserved layout space on every hover, displacing the block itself.
        // Production has no such marginTop; matching it here restores
        // zero-layout-shift hover, at the cost of the toolbar being free to
        // overlap the block above it — which is the correct, intended look.
      }}
    >
      {showToolbar && (
        <IconsView style={{ backgroundColor: color }}>
          {moveable && (
            <IconButton ref={drag} label="Drag to reorder block">
              <DragIndicatorIcon style={{ cursor: "move" }} />
            </IconButton>
          )}

          {wheelData?.length > 1 && (
            <IconButton onClick={handleMove} label="Move block to another segment">
              <MoveIcon />
            </IconButton>
          )}

          <IconButton onClick={onEditClick} label="Edit block">
            <EditIcon />
          </IconButton>

          <IconButton onClick={(e) => handleClone(e, id)} label="Duplicate block">
            <ContentCopyIcon />
          </IconButton>
          {deletable && (
            <IconButton onClick={onDeleteClick} label="Delete block">
              <DeleteIcon />
            </IconButton>
          )}
        </IconsView>
      )}
      {children}
    </div>
  );
};
